// Plugin-Fenster im Mixer. EQ mit Kurve, Spektrum und ziehbaren Bändern; Kompressor mit Kennlinie und
// Gain-Reduction; die übrigen Plugins mit Drehreglern. Jede Änderung ist ein Rückgängig-Schritt und geht live an den Raum.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useStudio } from '../state/store.ts';
import { engine } from '../audio/engine.ts';
import { pluginDef, resolveParams, PLUGINS, type ParamDef, type PluginDef, type Params, type PluginInstance } from '../audio/plugins.ts';
import { channelInserts, channelName, mutInserts, SLOT_LETTERS } from '../state/mixer.ts';
import { Control, Knob, Choice, Switch, fmtParam } from '../components/controls.tsx';
import { useBridge } from './Dante.tsx';
import { insOverrides, trackLike } from '../edit/automation.ts';
import { useEdit } from '../state/edit.ts';
import type { Track } from '@srs/shared';
import { BridgePanel } from './BridgePlugin.tsx';
import { pluginLabel } from '../audio/bridgefx.ts';

/** Automatisierte Werte eines Inserts an der Abspielposition (Read), sonst undefined */
function useInsAuto(t: Track | undefined, slotId: string | undefined) {
  const has = !!t && !!slotId && (t.automation?.mode ?? 'off') !== 'off' && Object.entries(t.automation!.lanes).some(([k, l]) => l.length && k.startsWith(`ins:${slotId}:`));
  const read = () => (has ? insOverrides(t!, engine.position, engine.manual)?.[slotId!] : undefined);
  const [v, setV] = useState(read);
  const ref = useRef(read); ref.current = read;
  useEffect(() => {
    if (!has) { setV(undefined); return; }
    let raf = 0, last = '';
    const loop = () => { const x = ref.current(), k = JSON.stringify(x ?? null); if (k !== last) { last = k; setV(x); } raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [has]);
  return has ? v : undefined;
}

/** Reglerbewegungen im Plugin-Fenster als Touch an die Automation melden; Loslassen beendet den Touch */
function useInsTouch(channel: string) {
  const held = useRef(new Set<string>());
  const down = useRef(false);
  useEffect(() => {
    const d = () => { down.current = true; };
    const u = () => { down.current = false; for (const k of held.current) engine.touch(channel, k, false); held.current.clear(); };
    window.addEventListener('pointerdown', d, true); window.addEventListener('pointerup', u, true);
    return () => { window.removeEventListener('pointerdown', d, true); window.removeEventListener('pointerup', u, true); u(); };
  }, [channel]);
  return (param: string) => {
    if (held.current.has(param)) return;
    held.current.add(param);
    engine.touch(channel, param, true);
    if (!down.current) setTimeout(() => { if (!down.current && held.current.delete(param)) engine.touch(channel, param, false); }, 300);
  };
}

export function PluginWindow() {
  const win = useStudio((s) => s.pluginWin)!;
  const project = useStudio((s) => s.project);
  const commit = useStudio((s) => s.commit);
  const openPlugin = useStudio((s) => s.openPlugin);
  const bridge = useBridge();
  const list = channelInserts(project, win.channel);
  const index = list.findIndex((s) => s.id === win.slotId);
  const slot = list[index];
  const [pos, setPos] = useState(() => ({ x: Math.max(20, window.innerWidth / 2 - 380), y: 90 }));
  const drag = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  useEffect(() => { if (!slot) openPlugin(null); }, [slot, openPlugin]);
  const chTrack = trackLike(project, win.channel);
  const autoV = useInsAuto(chTrack, slot?.id);
  const touchParam = useInsTouch(win.channel);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !(e.target as HTMLElement).closest('input, select')) { e.stopImmediatePropagation(); openPlugin(null); } };
    window.addEventListener('keydown', esc, true);
    return () => window.removeEventListener('keydown', esc, true);
  }, [openPlugin]);
  if (!slot) return null;
  const def = pluginDef(slot.plugin)!;
  const params = autoV ? { ...resolveParams(def, slot.params), ...autoV } : resolveParams(def, slot.params);
  const label = (k: string) => def.params.find((d) => d.key === k)?.label ?? k;
  const autoKeys = Object.entries(chTrack?.automation?.lanes ?? {}).filter(([k, l]) => l.length && k.startsWith(`ins:${slot.id}:`)).map(([k]) => k);
  const showAuto = () => {
    if (!chTrack) return;
    const e = useEdit.getState(), cur = e.autoLanes[win.channel] ?? [];
    const add = autoKeys.length ? autoKeys : def.params[0] ? [`ins:${slot.id}:${def.params[0].key}`] : [];
    e.set({ autoLanes: { ...e.autoLanes, [win.channel]: [...cur, ...add.filter((k) => !cur.includes(k))] } });
    useStudio.getState().setMixerOpen(false); useStudio.getState().setView('session');
  };
  const set = (patch: Params, what = Object.keys(patch).map(label).join(', ')) => { if (chTrack) for (const k of Object.keys(patch)) if (patch[k] !== params[k]) touchParam(`ins:${slot.id}:${k}`); commit(`${def.short}: ${what}`, (p) => {
    const s = mutInserts(p, win.channel).find((x) => x.id === slot.id);
    if (!s) return;
    s.params = { ...resolveParams(def, s.params), ...patch };
    s.ai = false;
  }, { merge: true }); };
  const bridgeName = slot.plugin.startsWith('bridge:') ? bridge.plugins.find((b) => `bridge:${b.id}` === slot.plugin) : undefined;

  return (
    <div className={`pw-win ${def.id.replace(':', '-')}`} style={{ left: pos.x, top: pos.y }} onPointerDown={(e) => e.stopPropagation()}>
      <div className="pw-head"
        onPointerDown={(e) => { if ((e.target as HTMLElement).closest('button, select')) return; drag.current = { x: e.clientX, y: e.clientY, px: pos.x, py: pos.y }; (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); }}
        onPointerMove={(e) => { const d = drag.current; if (d) setPos({ x: Math.max(0, d.px + e.clientX - d.x), y: Math.max(0, d.py + e.clientY - d.y) }); }}
        onPointerUp={() => { drag.current = null; }}>
        <div className="pw-title">
          <b>{bridgeName ? pluginLabel(bridgeName.vendor, bridgeName.name) : slot.label ?? def.name}</b>
          <span>{channelName(project, win.channel)} · Insert {SLOT_LETTERS[index]}</span>
        </div>
        {slot.ai && <span className="pill violet" title="Von der KI gesetzt. Jede Änderung von euch nimmt die Kennzeichnung weg.">KI-Vorschlag</span>}
        <div className="r">
          {def.presets.length > 0 && (
            <select className="input" value="" onChange={(e) => { const pr = def.presets.find((x) => x.name === e.target.value); if (pr) commit(`${def.short}: Preset ${pr.name}`, (p) => { const s = mutInserts(p, win.channel).find((x) => x.id === slot.id); if (s) { s.params = resolveParams(def, pr.params); s.ai = false; } }); }}>
              <option value="">Presets …</option>
              {def.presets.map((pr) => <option key={pr.name}>{pr.name}</option>)}
            </select>
          )}
          {def.params.length > 0 && chTrack && <button className={`btn small ${autoKeys.length ? 'on' : ''}`} title="Automation-Kurven dieses Plugins im Edit-Fenster zeigen. In Touch, Latch oder Write werden Reglerbewegungen während der Wiedergabe aufgezeichnet." onClick={showAuto}>Automation{autoKeys.length ? ` (${autoKeys.length})` : ''}</button>}
          {def.params.length > 0 && <button className="btn small" title="Alle Werte auf Standard" onClick={() => commit(`${def.short}: Standard`, (p) => { const s = mutInserts(p, win.channel).find((x) => x.id === slot.id); if (s) { s.params = resolveParams(def); s.ai = false; } })}>Standard</button>}
          <button className={`btn small ${slot.bypass ? 'danger' : ''}`} onClick={() => commit(slot.bypass ? 'Insert aktiv' : 'Insert Bypass', (p) => { const s = mutInserts(p, win.channel).find((x) => x.id === slot.id); if (s) s.bypass = !s.bypass; })}>{slot.bypass ? 'Bypass an' : 'Bypass'}</button>
          <button className="btn small" title="Schließen (Esc)" onClick={() => openPlugin(null)}>✕</button>
        </div>
      </div>
      <div className="pw-body">
        {def.id === 'srs:eq' ? <EqEditor def={def} p={params} set={set} channel={win.channel} slotId={slot.id} bypass={slot.bypass} />
          : def.id === 'srs:comp' ? <CompEditor def={def} p={params} set={set} channel={win.channel} slotId={slot.id} />
            : def.group === 'Bridge' ? <BridgePanel channel={win.channel} slot={slot} />
              : <GenericEditor def={def} p={params} set={set} channel={win.channel} slotId={slot.id} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EQ
const F_MIN = 20, F_MAX = 20000, G_MAX = 18;
const BANDS: { key: string; name: string; color: string; kind: 'hp' | 'lp' | 'ls' | 'hs' | 'bell' }[] = [
  { key: 'hp', name: 'Low-Cut', color: '#5a6478', kind: 'hp' },
  { key: 'ls', name: 'Low-Shelf', color: '#e23e2c', kind: 'ls' },
  { key: 'b1', name: 'Band 1', color: '#c7861b', kind: 'bell' },
  { key: 'b2', name: 'Band 2', color: '#1f8a5a', kind: 'bell' },
  { key: 'b3', name: 'Band 3', color: '#0f8b8d', kind: 'bell' },
  { key: 'hs', name: 'High-Shelf', color: '#2f5fd0', kind: 'hs' },
  { key: 'lp', name: 'High-Cut', color: '#6b4fbb', kind: 'lp' },
];

/** Kurve unabhängig von der laufenden Wiedergabe berechnen: eigener EQ in einem kleinen Offline-Kontext */
let curveCtx: OfflineAudioContext | null = null;
let curveEq: PluginInstance | null = null;
function eqCurve(p: Params, freqs: Float32Array<ArrayBuffer>): Float32Array {
  curveCtx ??= new OfflineAudioContext(2, 1, 48000);
  curveEq ??= PLUGINS.find((d) => d.id === 'srs:eq')!.create(curveCtx, p, { bpm: 120, offline: true });
  curveEq.set(p, { bpm: 120, offline: true });
  const out = new Float32Array(freqs.length), mag = new Float32Array(freqs.length), ph = new Float32Array(freqs.length);
  for (const f of curveEq.filters!) {
    f.getFrequencyResponse(freqs, mag, ph);
    for (let i = 0; i < freqs.length; i++) out[i] += 20 * Math.log10(Math.max(1e-6, mag[i]));
  }
  for (let i = 0; i < out.length; i++) out[i] += p.out;
  return out;
}

function EqEditor({ def, p, set, channel, slotId, bypass }: { def: PluginDef; p: Params; set: (patch: Params, what?: string) => void; channel: string; slotId: string; bypass: boolean }) {
  const wrap = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const drag = useRef<{ band: string } | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  const pRef = useRef(p); pRef.current = p;
  const freqs = useMemo(() => Float32Array.from({ length: 480 }, (_, i) => F_MIN * Math.pow(F_MAX / F_MIN, i / 479)), []);
  const d = (k: string) => def.params.find((x) => x.key === k)!;

  const geo = () => { const W = wrap.current?.clientWidth ?? 640, H = wrap.current?.clientHeight ?? 250; return { W, H }; };
  const fx = (f: number, W: number) => (Math.log(f / F_MIN) / Math.log(F_MAX / F_MIN)) * W;
  const xf = (x: number, W: number) => F_MIN * Math.pow(F_MAX / F_MIN, Math.max(0, Math.min(1, x / W)));
  const gy = (g: number, H: number) => H / 2 - (g / G_MAX) * (H / 2 - 12);
  const yg = (y: number, H: number) => Math.max(-G_MAX, Math.min(G_MAX, ((H / 2 - y) / (H / 2 - 12)) * G_MAX));
  const handle = (b: (typeof BANDS)[number], q: Params, W: number, H: number) => {
    const f = q[`${b.key}F`], on = q[`${b.key}On`] > 0.5;
    const g = b.kind === 'hp' || b.kind === 'lp' ? 0 : q[`${b.key}G`];
    return { x: fx(f, W), y: gy(g, H), on };
  };

  useEffect(() => {
    let raf = 0;
    const spec = new Float32Array(4096);
    const draw = () => {
      const c = canvas.current, w = wrap.current;
      if (c && w) {
        const dpr = devicePixelRatio || 1, { W, H } = geo();
        if (c.width !== W * dpr || c.height !== H * dpr) { c.width = W * dpr; c.height = H * dpr; c.style.width = `${W}px`; c.style.height = `${H}px`; }
        const g = c.getContext('2d')!;
        g.setTransform(dpr, 0, 0, dpr, 0, 0);
        g.fillStyle = '#0b1530'; g.fillRect(0, 0, W, H);
        // Raster
        g.font = '500 10px "IBM Plex Mono"';
        for (const f of [30, 50, 100, 200, 300, 500, 1000, 2000, 3000, 5000, 10000, 20000]) {
          const x = Math.round(fx(f, W)) + 0.5;
          g.fillStyle = 'rgba(255,255,255,.07)'; g.fillRect(x, 0, 1, H);
          if ([50, 100, 200, 500, 1000, 2000, 5000, 10000].includes(f)) { g.fillStyle = 'rgba(255,255,255,.4)'; g.fillText(f >= 1000 ? `${f / 1000}k` : String(f), x + 3, H - 5); }
        }
        for (let db = -G_MAX; db <= G_MAX; db += 6) {
          const y = Math.round(gy(db, H)) + 0.5;
          g.fillStyle = db === 0 ? 'rgba(255,255,255,.22)' : 'rgba(255,255,255,.07)'; g.fillRect(0, y, W, 1);
          if (db !== -G_MAX && db !== G_MAX) { g.fillStyle = 'rgba(255,255,255,.4)'; g.fillText(`${db > 0 ? '+' : ''}${db}`, 4, y - 3); }
        }
        // Spektrum hinter dem EQ (nur während der Wiedergabe)
        const inst = engine.plugin(channel, slotId);
        if (inst?.analyser && useStudio.getState().transport.playing) {
          const an = inst.analyser, bins = an.frequencyBinCount, sr = an.context.sampleRate;
          an.getFloatFrequencyData(spec as Float32Array<ArrayBuffer>);
          g.beginPath(); g.moveTo(0, H);
          for (let x = 0; x <= W; x += 2) {
            const f0 = xf(x, W), f1 = xf(x + 2, W);
            const b0 = Math.max(1, Math.floor((f0 / (sr / 2)) * bins)), b1 = Math.max(b0 + 1, Math.ceil((f1 / (sr / 2)) * bins));
            let m = -140; for (let k = b0; k < b1 && k < bins; k++) m = Math.max(m, spec[k]);
            // Spektrum mit 4,5 dB/Okt. Neigung, damit es wie in gängigen Analyzern aussieht
            const tilt = 4.5 * Math.log2(f0 / 1000);
            const y = H - Math.max(0, Math.min(1, (m + tilt + 90) / 90)) * H;
            g.lineTo(x, y);
          }
          g.lineTo(W, H); g.closePath();
          g.fillStyle = 'rgba(120,160,255,.18)'; g.fill();
        }
        // Kurve
        const q = pRef.current, cv = eqCurve(q, freqs);
        g.beginPath();
        for (let i = 0; i < freqs.length; i++) { const x = fx(freqs[i], W), y = gy(Math.max(-G_MAX - 6, Math.min(G_MAX + 6, cv[i])), H); if (i) g.lineTo(x, y); else g.moveTo(x, y); }
        g.strokeStyle = bypass ? 'rgba(255,255,255,.35)' : '#ffd36b'; g.lineWidth = 2.2; g.stroke();
        g.lineTo(W, gy(0, H)); g.lineTo(0, gy(0, H)); g.closePath(); g.fillStyle = bypass ? 'rgba(255,255,255,.04)' : 'rgba(255,211,107,.10)'; g.fill();
        // Griffe
        for (const b of BANDS) {
          const h = handle(b, q, W, H);
          g.beginPath(); g.arc(h.x, h.y, hover === b.key ? 9 : 7, 0, Math.PI * 2);
          g.fillStyle = h.on ? b.color : 'rgba(255,255,255,.12)'; g.fill();
          g.strokeStyle = '#fff'; g.lineWidth = h.on ? 2 : 1; g.stroke();
          g.fillStyle = '#fff'; g.font = '700 9px "IBM Plex Sans"'; g.textAlign = 'center';
          g.fillText(b.kind === 'hp' ? 'L' : b.kind === 'lp' ? 'H' : b.kind === 'ls' ? 'S' : b.kind === 'hs' ? 'S' : b.key.slice(1), h.x, h.y + 3);
          g.textAlign = 'left';
        }
        if (hover) {
          const b = BANDS.find((x) => x.key === hover)!, f = q[`${b.key}F`];
          const txt = `${b.name}: ${fmtParam(d(`${b.key}F`), f)}${b.kind === 'hp' || b.kind === 'lp' ? `, ${['12', '24', '36', '48'][Math.round(q[`${b.key}Slope`])]} dB/Okt.` : `, ${fmtParam(d(`${b.key}G`), q[`${b.key}G`])}`}${b.kind === 'bell' ? `, Q ${q[`${b.key}Q`].toFixed(2).replace('.', ',')}` : ''}`;
          g.font = '600 11.5px "IBM Plex Sans"'; const tw = g.measureText(txt).width;
          g.fillStyle = 'rgba(7,16,38,.92)'; g.fillRect(W - tw - 22, 8, tw + 14, 22); g.fillStyle = '#fff'; g.fillText(txt, W - tw - 15, 23);
        }
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  });

  const hitBand = (x: number, y: number) => {
    const { W, H } = geo();
    let best: string | null = null, dist = 14;
    for (const b of BANDS) { const h = handle(b, pRef.current, W, H); const dd = Math.hypot(h.x - x, h.y - y); if (dd < dist) { dist = dd; best = b.key; } }
    return best;
  };
  const local = (e: { clientX: number; clientY: number }) => { const r = wrap.current!.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  const moveBand = (band: string, x: number, y: number) => {
    const { W, H } = geo(), b = BANDS.find((z) => z.key === band)!;
    const fd = d(`${band}F`), f = Math.round(Math.max(fd.min, Math.min(fd.max, xf(x, W))));
    const patch: Params = { [`${band}F`]: f, [`${band}On`]: 1 };
    if (b.kind !== 'hp' && b.kind !== 'lp') patch[`${band}G`] = Math.round(yg(y, H) * 10) / 10;
    set(patch, b.name);
  };

  return (
    <div className="eq">
      <div className="eq-graph" ref={wrap}
        onPointerDown={(e) => { const { x, y } = local(e); const b = hitBand(x, y); if (b) { drag.current = { band: b }; (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } }}
        onPointerMove={(e) => { const { x, y } = local(e); if (drag.current) moveBand(drag.current.band, x, y); else { const b = hitBand(x, y); if (b !== hover) setHover(b); (e.currentTarget as HTMLElement).style.cursor = b ? 'grab' : 'default'; } }}
        onPointerUp={() => { drag.current = null; }}
        onPointerLeave={() => { if (!drag.current) setHover(null); }}
        onDoubleClick={(e) => { const { x, y } = local(e); const b = hitBand(x, y); if (b) set({ [`${b}On`]: pRef.current[`${b}On`] > 0.5 ? 0 : 1 }, BANDS.find((z) => z.key === b)!.name); }}
        onWheel={(e) => {
          const { x, y } = local(e); const b = hitBand(x, y) ?? hover; if (!b) return;
          e.preventDefault();
          const band = BANDS.find((z) => z.key === b)!, q = pRef.current;
          if (band.kind === 'bell') set({ [`${b}Q`]: Math.round(Math.max(0.1, Math.min(18, q[`${b}Q`] * (e.deltaY < 0 ? 1.12 : 1 / 1.12))) * 100) / 100 }, `${band.name} Q`);
          else if (band.kind === 'hp' || band.kind === 'lp') set({ [`${b}Slope`]: Math.max(0, Math.min(3, Math.round(q[`${b}Slope`]) + (e.deltaY < 0 ? 1 : -1))) }, `${band.name} Steilheit`);
        }}>
        <canvas ref={canvas} />
      </div>
      <div className="eq-bands">
        {BANDS.map((b) => (
          <div key={b.key} className={`eq-band ${p[`${b.key}On`] > 0.5 ? '' : 'off'}`} onMouseEnter={() => setHover(b.key)} onMouseLeave={() => setHover(null)}>
            <button className="eq-on" style={{ background: p[`${b.key}On`] > 0.5 ? b.color : undefined }} onClick={() => set({ [`${b.key}On`]: p[`${b.key}On`] > 0.5 ? 0 : 1 }, b.name)}>{b.name}</button>
            <Knob d={d(`${b.key}F`)} value={p[`${b.key}F`]} onChange={(v) => set({ [`${b.key}F`]: v, [`${b.key}On`]: 1 }, b.name)} size={40} label="Frequenz" color={b.color} />
            {b.kind === 'hp' || b.kind === 'lp'
              ? <select className="input slope" value={Math.round(p[`${b.key}Slope`])} onChange={(e) => set({ [`${b.key}Slope`]: Number(e.target.value) }, `${b.name} Steilheit`)}>{['12', '24', '36', '48'].map((s, i) => <option key={s} value={i}>{s} dB/Okt.</option>)}</select>
              : <Knob d={d(`${b.key}G`)} value={p[`${b.key}G`]} onChange={(v) => set({ [`${b.key}G`]: v, [`${b.key}On`]: 1 }, b.name)} size={40} label="Gain" color={b.color} />}
            {b.kind === 'bell' && <Knob d={d(`${b.key}Q`)} value={p[`${b.key}Q`]} onChange={(v) => set({ [`${b.key}Q`]: v }, `${b.name} Q`)} size={40} label="Q" color={b.color} />}
          </div>
        ))}
        <div className="eq-band out"><div className="eq-on static">Ausgang</div><Knob d={d('out')} value={p.out} onChange={(v) => set({ out: v })} size={40} label="Gain" /></div>
      </div>
      <div className="pw-foot">Punkte ziehen: Frequenz und Gain · Mausrad auf einem Punkt: Güte (Q) bzw. Steilheit · Doppelklick: Band an/aus · Spektrum läuft während der Wiedergabe</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Kompressor
function useMeter(channel: string, slotId: string) {
  const [m, setM] = useState({ gr: 0, inDb: -120, outDb: -120 });
  useEffect(() => {
    let raf = 0, hold = 0;
    const loop = () => {
      const x = engine.plugin(channel, slotId)?.meter;
      if (x) { hold = Math.min(x.gr, hold * 0.92 + x.gr * 0.08); setM({ ...x, gr: Math.min(x.gr, hold) }); }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [channel, slotId]);
  return m;
}

function GrMeter({ gr, range = 24 }: { gr: number; range?: number }) {
  const ticks = range > 12 ? [0, 3, 6, 10, 15, 20] : [0, 2, 4, 6, 8, 10, 12];
  return (
    <div className="grm" title="Gain-Reduction">
      <div className="grm-v"><span>Gain-Reduction</span><b className="mono">{gr < -0.05 ? `−${(-gr).toFixed(1).replace('.', ',')} dB` : '0,0 dB'}</b></div>
      <div className="grm-bar"><i style={{ width: `${Math.min(100, (-gr / range) * 100)}%` }} /></div>
      <div className="grm-ticks">{ticks.map((t) => <span key={t} style={{ right: `${(t / range) * 100}%` }}>{t ? `−${t}` : '0'}</span>)}</div>
    </div>
  );
}

function CompEditor({ def, p, set, channel, slotId }: { def: PluginDef; p: Params; set: (patch: Params, what?: string) => void; channel: string; slotId: string }) {
  const m = useMeter(channel, slotId);
  const d = (k: string) => def.params.find((x) => x.key === k)!;
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const S = 210, dpr = devicePixelRatio || 1;
    c.width = S * dpr; c.height = S * dpr; c.style.width = `${S}px`; c.style.height = `${S}px`;
    const g = c.getContext('2d')!; g.setTransform(dpr, 0, 0, dpr, 0, 0);
    const lo = -60, x = (db: number) => ((db - lo) / -lo) * S, y = (db: number) => S - ((db - lo) / -lo) * S;
    g.fillStyle = '#0b1530'; g.fillRect(0, 0, S, S);
    g.strokeStyle = 'rgba(255,255,255,.08)'; for (let k = -50; k < 0; k += 10) { g.beginPath(); g.moveTo(x(k), 0); g.lineTo(x(k), S); g.moveTo(0, y(k)); g.lineTo(S, y(k)); g.stroke(); }
    g.strokeStyle = 'rgba(255,255,255,.25)'; g.setLineDash([3, 3]); g.beginPath(); g.moveTo(0, S); g.lineTo(S, 0); g.stroke(); g.setLineDash([]);
    const T = p.thr, R = Math.max(1, p.ratio), W = p.knee;
    const curve = (xi: number) => { const dd = xi - T; if (2 * dd < -W) return xi; if (W > 0 && 2 * Math.abs(dd) <= W) return xi + ((1 / R - 1) * (dd + W / 2) ** 2) / (2 * W); return T + dd / R; };
    g.strokeStyle = '#ffd36b'; g.lineWidth = 2.2; g.beginPath();
    for (let xi = lo; xi <= 0; xi += 0.5) { const yy = y(curve(xi)); if (xi === lo) g.moveTo(x(xi), yy); else g.lineTo(x(xi), yy); }
    g.stroke();
    g.fillStyle = 'rgba(255,211,107,.6)'; g.fillRect(x(T) - 0.5, 0, 1, S);
    // aktueller Pegel
    if (m.inDb > lo) { const xi = Math.min(0, m.inDb); g.beginPath(); g.arc(x(xi), y(curve(xi)), 5, 0, Math.PI * 2); g.fillStyle = '#fff'; g.fill(); }
    g.fillStyle = 'rgba(255,255,255,.45)'; g.font = '500 10px "IBM Plex Mono"'; g.fillText('Eingang →', 6, S - 6); g.save(); g.translate(12, 70); g.rotate(-Math.PI / 2); g.fillText('Ausgang →', 0, 0); g.restore();
  });
  return (
    <div className="comp">
      <div className="comp-l">
        <canvas ref={ref} />
        <GrMeter gr={m.gr} />
      </div>
      <div className="comp-r">
        <Choice d={d('char')} value={p.char} onChange={(v) => set({ char: v })} />
        <div className="knobs">
          {['thr', 'ratio', 'atk', 'rel', 'knee', 'makeup', 'mix', 'schp'].map((k) => <Knob key={k} d={d(k)} value={p[k]} onChange={(v) => set({ [k]: v })} size={50} />)}
        </div>
        <div className="row" style={{ gap: 10 }}>
          <Choice d={d('det')} value={p.det} onChange={(v) => set({ det: v })} />
          <Switch d={d('auto')} value={p.auto} onChange={(v) => set({ auto: v })} />
        </div>
        <div className="t2" style={{ fontSize: 11.5 }}>{['Clean: neutral, folgt genau den Reglern.', 'Opto: weich, RMS, lässt bei lang anhaltender Reduktion langsamer los. Gut für Gesang und Bass.', 'Punch: schnell, Spitzenwerte, leichte Sättigung. Gut für Drums und nach vorn gemischten Gesang.', 'Glue: Bus-Kompressor, RMS, Auto-Release. Hält den Mix zusammen.'][Math.round(p.char)]}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function GenericEditor({ def, p, set, channel, slotId }: { def: PluginDef; p: Params; set: (patch: Params, what?: string) => void; channel: string; slotId: string }) {
  const dyn = def.group === 'Dynamik';
  const m = useMeter(channel, slotId);
  const ctl = (d: ParamDef) => {
    if (def.id === 'srs:delay' && d.key === 'ms' && Math.round(p.div) !== 6) return null;
    return <Control key={d.key} d={d} value={p[d.key]} onChange={(v) => set({ [d.key]: v })} size={52} />;
  };
  const choices = def.params.filter((d) => d.choices), rest = def.params.filter((d) => !d.choices && !d.bool), bools = def.params.filter((d) => d.bool);
  return (
    <div className="generic">
      {choices.length > 0 && <div className="row" style={{ gap: 14, flexWrap: 'wrap' }}>{choices.map(ctl)}</div>}
      <div className="knobs">{rest.map(ctl)}</div>
      {bools.length > 0 && <div className="row" style={{ gap: 8 }}>{bools.map(ctl)}</div>}
      {def.id === 'srs:saturation' && <SatCurve type={p.type} drive={p.drive} />}
      {dyn && <GrMeter gr={m.gr} range={def.id === 'srs:gate' ? 60 : def.id === 'srs:deesser' ? 12 : 24} />}
      {def.id === 'srs:limiter' && <div className="t2" style={{ fontSize: 11.5 }}>Lookahead 1,5 ms, kein Sample über der Ceiling. Für Streaming −1 dB Ceiling, Lautheit am Master-Kanal ablesen.</div>}
      {def.id === 'srs:deesser' && <div className="t2" style={{ fontSize: 11.5 }}>Abhören schaltet auf das, was der De-Esser hört (das Band über der Frequenz). So findet ihr die Zischfrequenz.</div>}
    </div>
  );
}

function SatCurve({ type, drive }: { type: number; drive: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const S = 120, dpr = devicePixelRatio || 1;
    c.width = S * dpr; c.height = S * dpr; c.style.width = `${S}px`; c.style.height = `${S}px`;
    const g = c.getContext('2d')!; g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.fillStyle = '#0b1530'; g.fillRect(0, 0, S, S);
    g.strokeStyle = 'rgba(255,255,255,.2)'; g.beginPath(); g.moveTo(0, S); g.lineTo(S, 0); g.stroke();
    const k = Math.pow(10, drive / 20);
    const f = (x: number) => (Math.round(type) === 1 ? (x >= 0 ? Math.tanh(k * x) / k : Math.tanh(k * 1.35 * x) / (k * 1.35)) : Math.round(type) === 2 ? x / Math.pow(1 + Math.pow(Math.abs(k * x), 6), 1 / 6) : Math.tanh(k * x) / k);
    g.strokeStyle = '#ffd36b'; g.lineWidth = 2; g.beginPath();
    for (let i = 0; i <= S; i++) { const x = (i / S) * 2 - 1, y = f(x); const py = S / 2 - y * (S / 2); if (i) g.lineTo(i, py); else g.moveTo(i, py); }
    g.stroke();
  }, [type, drive]);
  return <div className="row" style={{ gap: 10, alignItems: 'center' }}><canvas ref={ref} /><span className="t2" style={{ fontSize: 11.5 }}>Kennlinie. Leise Anteile bleiben unverändert, laute bekommen Obertöne. 4-fach überabgetastet gegen Aliasing.</span></div>;
}

